import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import InstagramIcon from '@mui/icons-material/Instagram';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

const TikTokIcon = ({ color = "#000000" }) => {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="100%" 
      height="100%" 
      fillRule="evenodd" 
      clipRule="evenodd" 
      imageRendering="optimizeQuality" 
      shapeRendering="geometricPrecision" 
      textRendering="geometricPrecision" 
      viewBox="0 0 512 512"
      fill={color}
    >
      <path d="M353.97 0.43c0.03,7.81 2.31,120.68 120.76,127.72 0,32.55 0.04,56.15 0.04,87.21 -8.97,0.52 -77.98,-4.49 -120.93,-42.8l-0.13 169.78c1.63,117.84 -85.09,189.55 -198.44,164.78 -195.46,-58.47 -130.51,-348.37 65.75,-317.34 0,93.59 0.05,-0.03 0.05,93.59 -81.08,-11.93 -108.2,55.52 -86.65,103.81 19.6,43.97 100.33,53.5 128.49,-8.53 3.19,-12.14 4.78,-25.98 4.78,-41.52l0 -337.13 86.28 0.43z"></path>
    </svg>
  );
};

function SeccionContacto({ profesionalInfo, isMobile }) {
  const contactoRedes = profesionalInfo?.perfil?.contacto_redes || {};
  const nombreProfesional = profesionalInfo?.perfil?.profesional_nombre_plataforma || "";

  // Estado del bottom sheet de correo
  const [showEmailSheet, setShowEmailSheet] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const sheetRef = useRef(null);

  // Función para cerrar con animación
  const closeSheet = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setShowEmailSheet(false);
      setIsClosing(false);
      setCopiado(false);
    }, 220);
  }, []);

  // Cerrar al hacer click fuera de la caja
  useEffect(() => {
    if (!showEmailSheet) return;

    const handleClickOutside = (e) => {
      if (sheetRef.current && !sheetRef.current.contains(e.target)) {
        closeSheet();
      }
    };

    // Pequeño delay para que el click que abrió el sheet no lo cierre inmediatamente
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showEmailSheet, closeSheet]);

  // Función para copiar correo
  const handleCopiarCorreo = useCallback(() => {
    const correo = contactoRedes.correo || "";
    navigator.clipboard.writeText(correo).then(() => {
      setCopiado(true);
      setTimeout(() => {
        closeSheet();
      }, 1000);
    }).catch(() => {
      // Fallback para navegadores que no soportan clipboard API
      const textArea = document.createElement("textarea");
      textArea.value = correo;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopiado(true);
      setTimeout(() => {
        closeSheet();
      }, 1000);
    });
  }, [contactoRedes.correo, closeSheet]);

  // Construir URL de WhatsApp
  const buildWhatsAppUrl = () => {
    const telefono = contactoRedes.telefono || "";
    const telefonoLimpio = telefono.replace(/\s/g, "");
    const mensaje = `Hola ${nombreProfesional}, vi tu perfil profesional en Clinifica y me gustaría saber más de ti`;
    return `https://api.whatsapp.com/send/?phone=${encodeURIComponent(telefonoLimpio)}&text=${encodeURIComponent(mensaje)}&type=phone_number&app_absent=0`;
  };

  // Construir URL de Instagram
  const buildInstagramUrl = () => {
    const usuario = contactoRedes.instagram || "";
    const usuarioLimpio = usuario.replace(/^@/, "");
    return `https://www.instagram.com/${usuarioLimpio}`;
  };

  // Construir URL de TikTok
  const buildTikTokUrl = () => {
    const usuario = contactoRedes.tiktok || "";
    const usuarioLimpio = usuario.replace(/^@/, "");
    return `https://www.tiktok.com/@${usuarioLimpio}`;
  };

  // Handler de click por canal
  const handleCanalClick = (canalId) => {
    switch (canalId) {
      case 'instagram':
        window.open(buildInstagramUrl(), "_blank");
        break;
      case 'tiktok':
        window.open(buildTikTokUrl(), "_blank");
        break;
      case 'telefono':
        window.open(buildWhatsAppUrl(), "_blank");
        break;
      case 'correo':
        setShowEmailSheet(true);
        setCopiado(false);
        break;
      default:
        break;
    }
  };

  // Tamaño de iconos
  const iconSize = { xs: "18px", md: "20px" };

  // Definir canales
  const canales = [
    { 
      id: 'instagram', 
      label: 'Instagram', 
      activo: contactoRedes.instagram_activo, 
      valor: contactoRedes.instagram,
      icon: <InstagramIcon sx={{ fontSize: iconSize, color: "#3B82F6" }} />
    },
    { 
      id: 'tiktok', 
      label: 'TikTok', 
      activo: contactoRedes.tiktok_activo, 
      valor: contactoRedes.tiktok,
      icon: <Box sx={{ width: iconSize, height: iconSize, display: "flex", alignItems: "center" }}><TikTokIcon color="#3B82F6" /></Box>
    },
    { 
      id: 'correo', 
      label: 'Email', 
      activo: contactoRedes.correo_activo, 
      valor: contactoRedes.correo,
      icon: <EmailOutlinedIcon sx={{ fontSize: iconSize, color: "#3B82F6" }} />
    },
    { 
      id: 'telefono', 
      label: 'WhatsApp', 
      activo: contactoRedes.telefono_activo, 
      valor: contactoRedes.telefono,
      icon: <WhatsAppIcon sx={{ fontSize: iconSize, color: "#3B82F6" }} />
    }
  ];

  const canalesActivos = canales.filter(canal => canal.activo);

  if (canalesActivos.length === 0) {
    return null;
  }

  return (
    <>
      <Typography
        sx={{
          mb: 1.5,
          mt: 1.5,
          fontFamily: "Quicksand-Bold",
          fontSize: { md: "20px", xs: "18px" },
        }}
      >
        Encuéntrame en
      </Typography>
      
      <Box
        sx={{
          display: { xs: "grid", md: "flex" },
          gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "none" },
          gap: 1,
          mb: 1.5,
        }}
      >
        {canalesActivos.map((canal) => (
          <Box
            key={canal.id}
            onClick={() => handleCanalClick(canal.id)}
            sx={{
              fontFamily: "Quicksand-Regular",
              fontSize: { md: "16px", xs: "14px" },
              borderRadius: 2,
              px: 1.5,
              py: 0.5,
              whiteSpace: "nowrap",
              backgroundColor: "#ffffff",
              border: "1px solid #E0E0E0",
              color: "#09090a",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              width: { xs: "100%", md: "195px" },
              maxWidth: { xs: "100%", md: "195px" },
              boxSizing: "border-box",
              cursor: "pointer",
              transition: "background-color 0.2s ease",
              userSelect: "none",
              "&:hover": {
                backgroundColor: "#f0f4ff",
              },
              "&:active": {
                backgroundColor: "#f0f4ff",
              },
            }}
          >
            {canal.icon}
            {canal.label}
          </Box>
        ))}
      </Box>

      {/* =============================== */}
      {/* BOTTOM SHEET DE CORREO           */}
      {/* =============================== */}
      {showEmailSheet && (
        <>
          {/* Backdrop oscuro */}
          <Box
            onClick={closeSheet}
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.35)",
              zIndex: 1299,
              animation: isClosing
                ? "fadeOutBackdropContacto 0.22s ease forwards"
                : "fadeInBackdropContacto 0.22s ease",
              "@keyframes fadeInBackdropContacto": {
                from: { opacity: 0 },
                to: { opacity: 1 },
              },
              "@keyframes fadeOutBackdropContacto": {
                from: { opacity: 1 },
                to: { opacity: 0 },
              },
            }}
          />
          <Box
            ref={sheetRef}
            sx={{
              position: "fixed",
              bottom: 0,
              left: isMobile ? 0 : "50%",
              transform: isMobile ? "none" : "translateX(-50%)",
              width: isMobile ? "100%" : "800px",
              maxWidth: isMobile ? "100%" : "800px",
              zIndex: 1300,
              backgroundColor: "#ffffff",
              borderTopLeftRadius: "20px",
              borderTopRightRadius: "20px",
              p: 2.5,
              boxSizing: "border-box",
              animation: isClosing 
                ? "slideDownContacto 0.22s ease-in forwards" 
                : "slideUpContacto 0.22s ease-out",
              "@keyframes slideUpContacto": {
                from: {
                  transform: isMobile 
                    ? "translateY(100%)" 
                    : "translateX(-50%) translateY(100%)",
                },
                to: {
                  transform: isMobile 
                    ? "translateY(0)" 
                    : "translateX(-50%) translateY(0)",
                },
              },
              "@keyframes slideDownContacto": {
                from: {
                  transform: isMobile 
                    ? "translateY(0)" 
                    : "translateX(-50%) translateY(0)",
                },
                to: {
                  transform: isMobile 
                    ? "translateY(100%)" 
                    : "translateX(-50%) translateY(100%)",
                },
              },
            }}
          >
            {/* Pill handle */}
            <Box sx={{ width: 36, height: 4, borderRadius: 2, backgroundColor: "#d1d5db", margin: "0 auto 20px" }} />

            {/* Campo con el correo */}
            <Box
              sx={{
                width: "100%",
                border: "1px solid #e5e7eb",
                borderRadius: "14px",
                px: 2,
                py: 1.5,
                fontFamily: "Quicksand-Regular",
                fontSize: { md: "16px", xs: "14px" },
                color: "#191C1F",
                backgroundColor: "#ffffff",
                boxSizing: "border-box",
                mb: 1.25,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textAlign: "center",
              }}
            >
              {contactoRedes.correo}
            </Box>

            {/* Botón copiar correo */}
            <Box
              onClick={handleCopiarCorreo}
              sx={{
                width: "100%",
                border: copiado ? "2px solid #3B82F6" : "2px solid transparent",
                borderRadius: "14px",
                px: 2,
                py: 1.5,
                fontFamily: "Quicksand-Bold",
                fontSize: { md: "16px", xs: "14px" },
                color: copiado ? "#3B82F6" : "#ffffff",
                backgroundColor: copiado ? "#EFF6FF" : "#3B82F6",
                boxSizing: "border-box",
                textAlign: "center",
                cursor: "pointer",
                transition: "all 0.2s ease",
                userSelect: "none",
                "&:hover": {
                  backgroundColor: copiado ? "#EFF6FF" : "#2563EB",
                },
                "&:active": {
                  backgroundColor: copiado ? "#EFF6FF" : "#2563EB",
                },
              }}
            >
              {copiado ? "Correo copiado" : "Copiar correo"}
            </Box>
          </Box>
        </>
      )}
    </>
  );
}

export default SeccionContacto;
